import React, { useState, useEffect, useCallback } from 'react';
import { 
  Calendar,
  Plus, 
  Trash2, 
  CheckCircle, 
  X,
  AlertCircle,
  MapPin,
  Edit
} from 'lucide-react';
import { 
  fetchClientSessions, 
  addClientSession, 
  updateSessionStatus, 
  deleteClientSession 
} from '../../lib/supabase';
import EditSessionModal from './EditSessionModal';
import { isDateTimeInPast } from '../../lib/utils/dateUtils';

const SessionManagement = ({ clientId, clientName, onSessionChange }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [isAddSessionModalOpen, setIsAddSessionModalOpen] = useState(false);
  const [isEditSessionModalOpen, setIsEditSessionModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [newSession, setNewSession] = useState({
    title: '',
    date: '',
    time: '',
    duration: 60,
    location: 'Online',
    notes: ''
  });
  const [formError, setFormError] = useState('');

  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchClientSessions(clientId);
      setSessions(data);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleAddSession = async (e) => {
    e.preventDefault();
    
    if (!newSession.title.trim()) {
      setFormError('Session title is required');
      return;
    }
    
    if (!newSession.date) {
      setFormError('Session date is required');
      return;
    }
    
    if (!newSession.time) {
      setFormError('Session time is required');
      return;
    }

    if (isDateTimeInPast(newSession.date, newSession.time)) {
        setFormError('Session time cannot be in the past');
        return;
      }
    
    try {
      // Combine date and time
      const sessionDateTime = new Date(`${newSession.date}T${newSession.time}`);
      
      await addClientSession({
        client_id: clientId,
        title: newSession.title, // This will be mapped to session_type
        session_date: sessionDateTime.toISOString(),
        status: 'scheduled',
        duration_minutes: newSession.duration,
        location: newSession.location,
        notes: newSession.notes
      });
      
      // Reset form
      setNewSession({
        title: '',
        date: '',
        time: '',
        duration: 60,
        location: 'Online',
        notes: ''
      });
      
      // Close modal
      setIsAddSessionModalOpen(false);
      setFormError('');
      
      // Refresh sessions
      await loadSessions();
      
      // Notify parent component
      if (onSessionChange) {
        onSessionChange();
      }
    } catch (err) {
      console.error('Error adding session:', err);
      setFormError('Failed to add session: ' + (err.message || 'Unknown error'));
    }
  };

  const handleEditSession = (session) => {
    setSelectedSession(session);
    setIsEditSessionModalOpen(true);
  };

  const handleMarkComplete = async (sessionId) => {
    try {
      await updateSessionStatus(sessionId, 'completed');
      
      // Update local state
      setSessions(prevSessions =>
        prevSessions.map(session =>
          session.id === sessionId ? { ...session, status: 'completed' } : session
        )
      );
      
      // Notify parent component
      if (onSessionChange) {
        onSessionChange();
      }
    } catch (err) {
      console.error('Error updating session status:', err);
    }
  };

  const handleDeleteSession = async () => {
    if (!selectedSessionId) return;
    
    try {
      await deleteClientSession(selectedSessionId);
      
      // Update local state
      setSessions(prevSessions =>
        prevSessions.filter(session => session.id !== selectedSessionId)
      );
      
      // Close modal
      setIsDeleteConfirmOpen(false);
      setSelectedSessionId(null);
      
      // Notify parent component
      if (onSessionChange) {
        onSessionChange();
      }
    } catch (err) {
      console.error('Error deleting session:', err);
    }
  };

  // Separate upcoming and past sessions
  const upcomingSessions = sessions.filter(session => 
    session.status === 'scheduled' && new Date(session.session_date) > new Date()
  ).sort((a, b) => new Date(a.session_date) - new Date(b.session_date));

  const pastSessions = sessions.filter(session => 
    session.status === 'completed' || 
    (session.status === 'scheduled' && new Date(session.session_date) < new Date())
  ).sort((a, b) => new Date(b.session_date) - new Date(a.session_date));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upcoming Sessions Section */}
      <div className="bg-white rounded-lg p-6 shadow-md border border-teal-500">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Upcoming Sessions</h3>
          <button
            onClick={() => setIsAddSessionModalOpen(true)}
            className="flex items-center px-3 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
          >
            <Plus size={18} className="mr-1" />
            Add Session
          </button>
        </div>

        {upcomingSessions.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            No upcoming sessions scheduled
          </div>
        ) : (
          <div className="space-y-3">
            {upcomingSessions.map(session => (
              <div 
                key={session.id} 
                className="flex items-center justify-between p-3 bg-teal-50 rounded-lg"
              >
                <div className="flex items-center">
                  <div className="bg-teal-100 p-2 rounded-full mr-3">
                    <Calendar className="h-5 w-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-teal-800">{session.session_type}</h4>
                    <p className="text-sm text-gray-600">
                      {new Date(session.session_date).toLocaleDateString(undefined, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })} at {new Date(session.session_date).toLocaleTimeString(undefined, {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </p>
                    {session.location && (
                      <p className="text-xs text-gray-500 flex items-center mt-1">
                        <MapPin className="h-3 w-3 mr-1" />
                        {session.location} • {session.duration_minutes} minutes
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditSession(session)}
                    className="p-1 text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded-full transition-colors"
                    title="Edit Session"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleMarkComplete(session.id)}
                    className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-full transition-colors"
                    title="Mark as Completed"
                  >
                    <CheckCircle className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedSessionId(session.id);
                      setIsDeleteConfirmOpen(true);
                    }}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                    title="Delete Session"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Past Sessions Section */}
      {pastSessions.length > 0 && (
        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-300">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Past Sessions</h3>
          <div className="space-y-3">
            {pastSessions.map(session => (
              <div 
                key={session.id} 
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center">
                  <div className="bg-gray-200 p-2 rounded-full mr-3">
                    <Calendar className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700">{session.session_type}</h4>
                    <p className="text-sm text-gray-500">
                      {new Date(session.session_date).toLocaleDateString(undefined, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })} at {new Date(session.session_date).toLocaleTimeString(undefined, {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </p>
                    <span className="inline-block px-2 py-1 mt-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {session.status === 'completed' ? 'Completed' : 'Passed'}
                    </span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditSession(session)}
                    className="p-1 text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded-full transition-colors"
                    title="Edit Session"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedSessionId(session.id);
                      setIsDeleteConfirmOpen(true);
                    }}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                    title="Delete Session"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Session Modal */}
      {isAddSessionModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setIsAddSessionModalOpen(false)} />

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-teal-600 px-4 py-3 sm:px-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-white">Schedule New Session</h3>
                  <button
                    onClick={() => setIsAddSessionModalOpen(false)}
                    className="text-white hover:text-gray-200"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                {formError && (
                  <div className="mb-4 p-2 bg-red-100 text-red-700 rounded flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    {formError}
                  </div>
                )}

                <form onSubmit={handleAddSession} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Session Title
                    </label>
                    <input
                      type="text"
                      value={newSession.title}
                      onChange={(e) => setNewSession({...newSession, title: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="e.g., Initial Consultation, Follow-up Meeting"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date
                      </label>
                      <input
                        type="date"
                        value={newSession.date}
                        onChange={(e) => setNewSession({...newSession, date: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Time
                      </label>
                      <input
                        type="time"
                        value={newSession.time}
                        onChange={(e) => setNewSession({...newSession, time: e.target.value})}
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Duration (minutes)
                      </label>
                      <select
                        value={newSession.duration}
                        onChange={(e) => setNewSession({...newSession, duration: parseInt(e.target.value, 10)})}
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      >
                        <option value="30">30 minutes</option>
                        <option value="45">45 minutes</option>
                        <option value="60">60 minutes</option>
                        <option value="90">90 minutes</option>
                        <option value="120">120 minutes</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Location
                      </label>
                      <select
                        value={newSession.location}
                        onChange={(e) => setNewSession({...newSession, location: e.target.value})}
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      >
                        <option value="Online">Online</option>
                        <option value="Office">Office</option>
                        <option value="Phone">Phone</option>
                        <option value="Client Location">Client Location</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={newSession.notes}
                      onChange={(e) => setNewSession({...newSession, notes: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      rows={3}
                      placeholder="Any additional details about this session"
                    />
                  </div>

                  <div className="mt-5 sm:mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setIsAddSessionModalOpen(false)}
                      className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-teal-600 text-base font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:text-sm"
                    >
                      Schedule Session
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Session Modal */}
      {isEditSessionModalOpen && selectedSession && (
        <EditSessionModal
          isOpen={isEditSessionModalOpen}
          onClose={() => {
            setIsEditSessionModalOpen(false);
            setSelectedSession(null);
          }}
          session={selectedSession}
          onUpdate={() => {
            loadSessions();
            if (onSessionChange) onSessionChange();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteConfirmOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setIsDeleteConfirmOpen(false)} />

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Session
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this session? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleDeleteSession}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsDeleteConfirmOpen(false);
                      setSelectedSessionId(null);
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionManagement;